// Compositing Calculation Utilities
// Economic modeling and interval compositing logic

export const defaultEconomicConfig = {
  payable: { cu: 0.965, au: 0.95, ag: 0.9 },
  recovery: { cu: 0.90, au: 0.80, ag: 0.80 }, // Updated to match historical processing results
  cappedRecovery: { cu: 0.98, au: 0.80, ag: 0.80 }, // Updated Au/Ag to 80%
  conversion: {
    cuLbPerTonne: 22.0462, // 1% Cu in a tonne -> lbs Cu (2204.62 lbs/tonne ÷ 100)
    auOzPerTonne: 0.032151, // g/t to troy oz/tonne (1 ÷ 31.1035)
    agOzPerTonne: 0.032151 // g/t to troy oz/tonne (1 ÷ 31.1035)
  },
  prices: {
    cuPerLb: 4.60,  // Updated from $4.25
    auPerOz: 3300,  // Updated from $2,500
    agPerOz: 40     // Updated from $29
  },
  cadUsd: 0.72,
  concentrateGradeCuPct: 0.38,
  offsiteUsdPerTonne: 256.18,
  royaltyPctNsr: 0.015,
  oxideRatioPct: 0
};

const safeDivide = (num, den) => (den === 0 ? 0 : num / den);

/**
 * Compute economic value breakdown for given metal grades
 * @param {Object} averages - {cu, au, ag} average grades
 * @param {Object} cfg - Economic configuration
 * @returns {Object} Value breakdown with economic metrics
 */
export function computeValue(averages, cfg) {
  const oxideFactor = Math.max(0, 1 - cfg.oxideRatioPct / 100);

  const cuPayRec =
    cfg.payable.cu *
    Math.min(cfg.recovery.cu, cfg.cappedRecovery.cu) *
    oxideFactor;
  const auPayRec =
    cfg.payable.au *
    Math.min(cfg.recovery.au, cfg.cappedRecovery.au) *
    oxideFactor;
  const agPayRec =
    cfg.payable.ag *
    Math.min(cfg.recovery.ag, cfg.cappedRecovery.ag) *
    oxideFactor;

  const valueCuUsdPerT =
    averages.cu *
    cfg.conversion.cuLbPerTonne *
    cfg.prices.cuPerLb *
    cuPayRec;

  const valueAuUsdPerT =
    averages.au *
    cfg.conversion.auOzPerTonne *
    cfg.prices.auPerOz *
    auPayRec;

  const valueAgUsdPerT =
    averages.ag *
    cfg.conversion.agOzPerTonne *
    cfg.prices.agPerOz *
    agPayRec;

  const grossUsd = valueCuUsdPerT + valueAuUsdPerT + valueAgUsdPerT;

  const nsrUsdPerT = Math.max(
    0,
    grossUsd * (1 - cfg.royaltyPctNsr) - cfg.offsiteUsdPerTonne
  );

  // CuEq calculation using gross values (industry standard formula)
  // CuEq = (Cu value + Au value + Ag value) / (Cu value per % per tonne)
  // Uses capped recovery only (no payable factors or offsite/royalty deductions)
  const cuRecOnly = cfg.cappedRecovery.cu * oxideFactor;
  const auRecOnly = cfg.cappedRecovery.au * oxideFactor;
  const agRecOnly = cfg.cappedRecovery.ag * oxideFactor;

  const cuValueGross = averages.cu * cfg.conversion.cuLbPerTonne * cfg.prices.cuPerLb * cuRecOnly;
  const auValueGross = averages.au * cfg.conversion.auOzPerTonne * cfg.prices.auPerOz * auRecOnly;
  const agValueGross = averages.ag * cfg.conversion.agOzPerTonne * cfg.prices.agPerOz * agRecOnly;

  const grossValueForCuEq = cuValueGross + auValueGross + agValueGross;
  const cuEqDenominator = cfg.conversion.cuLbPerTonne * cfg.prices.cuPerLb * cuRecOnly;

  const cuEqPct = safeDivide(grossValueForCuEq, cuEqDenominator);

  return { valueCuUsdPerT, valueAuUsdPerT, valueAgUsdPerT, nsrUsdPerT, cuEqPct };
}

/**
 * Composite an interval of assay samples
 * @param {Array} rows - Array of assay sample objects
 * @param {Object} cfg - Economic configuration
 * @returns {Object} Composite result with statistics and economics
 */
export function compositeInterval(rows, cfg = defaultEconomicConfig) {
  let totalLength = 0;
  let sumCu = 0;
  let sumAu = 0;
  let sumAg = 0;
  let pctMeters = 0;

  let fromDepthMin = null;
  let toDepthMax = null;
  let sampleNumberMin = null;
  let sampleNumberMax = null;

  rows.forEach((row) => {
    const len = Math.max(0, row.to_depth - row.from_depth);
    totalLength += len;
    sumCu += row.cu_best * len;
    sumAu += row.au_best * len;
    sumAg += row.ag_best * len;
    pctMeters += row.cu_best * len;

    fromDepthMin = fromDepthMin === null ? row.from_depth : Math.min(fromDepthMin, row.from_depth);
    toDepthMax = toDepthMax === null ? row.to_depth : Math.max(toDepthMax, row.to_depth);
    sampleNumberMin =
      sampleNumberMin === null ? row.sample_number : [sampleNumberMin, row.sample_number].sort()[0];
    sampleNumberMax =
      sampleNumberMax === null ? row.sample_number : [sampleNumberMax, row.sample_number].sort()[1];
  });

  const avgCu = safeDivide(sumCu, totalLength);
  const avgAu = safeDivide(sumAu, totalLength);
  const avgAg = safeDivide(sumAg, totalLength);

  const values = computeValue({ cu: avgCu, au: avgAu, ag: avgAg }, cfg);

  return {
    rowsCount: rows.length,
    totalLength: Number(totalLength.toFixed(3)),
    avgCu: Number(avgCu.toFixed(5)),
    avgAu: Number(avgAu.toFixed(5)),
    avgAg: Number(avgAg.toFixed(5)),
    pctMeters: Number(pctMeters.toFixed(3)),
    fromDepthMin,
    toDepthMax,
    sampleNumberMin,
    sampleNumberMax,
    valueCuUsdPerT: Number(values.valueCuUsdPerT.toFixed(2)),
    valueAuUsdPerT: Number(values.valueAuUsdPerT.toFixed(2)),
    valueAgUsdPerT: Number(values.valueAgUsdPerT.toFixed(2)),
    nsrUsdPerT: Number(values.nsrUsdPerT.toFixed(2)),
    cuEqPct: Number(values.cuEqPct.toFixed(5))
  };
}

/**
 * Generate rock type color palette
 * @param {Array} rows - Assay rows
 * @returns {Object} Map of rock type to color
 */
export const rockPalette = [
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#14b8a6',
  '#fb7185',
  '#f97316',
  '#6366f1',
  '#22d3ee'
];

export function rockColorMap(rows) {
  const unique = Array.from(new Set(rows.map((r) => r.rock_type).filter(rt => rt)));
  const map = {};
  unique.forEach((r, idx) => {
    map[r] = rockPalette[idx % rockPalette.length];
  });
  return map;
}

/**
 * Color scale for value visualization
 * @param {number} value - Current value
 * @param {number} max - Maximum value
 * @returns {string} RGB color string
 */
export function colorScale(value, max) {
  const v = max > 0 ? Math.min(value / max, 1) : 0;
  if (v <= 0.5) {
    const t = v / 0.5; // blue -> red
    const r = Math.round(255 * t);
    const g = 0;
    const b = Math.round(255 * (1 - t));
    return `rgb(${r}, ${g}, ${b})`;
  }
  const t = (v - 0.5) / 0.5; // red -> magenta
  const r = 255;
  const g = 0;
  const b = Math.round(255 * t);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Default header mapping for CSV import
 */
export const defaultHeaderMapping = {
  hole_id: 'hole_id',
  sample_number: 'sample_number',
  from_depth: 'from_depth',
  to_depth: 'to_depth',
  cu_best: 'cu_best',
  au_best: 'au_best',
  ag_best: 'ag_best',
  rock_type: 'rock_type'
};

